/** @format */

import React, { FunctionComponent, ReactElement, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import GridContainer from '../../../../../components/Grid/GridContainer';
import GridItem from '../../../../../components/Grid/GridItem';
import {
    SimCardServiceItemConfiguration,
    SimCardServiceTypeConfigurationGet,
    SimCardServiceTypeConfigurationPost,
    SimCardServiceTypeConfigurationPut,
    SimCardServiceTypeItem,
} from '../../../../../interfaces/collections/SimCardServiceInterface';
import I18n from '../../../../../components/Translate/I18n';
import CustomTextButtom from '../../../../../components/Buttons/CustomTextButton';
import { SimpleSelect } from '../../../../../components/Adescom/SimpleSelect';
import { getBooleanElementList } from '../../../../lib/search/listTableHeader/HeaderBooleanCheckBoxSelectList';
import {
    PACKAGE_ACTIVATE,
    PACKAGE_EXPIRE,
    PACKAGE_NEAR_EXPIRE,
    PACKAGE_USAGE_80_PERCENT,
    PACKAGE_USAGE_ALL,
} from '../../../../../modules/features/SmsRequestTemplateType';
import { ItemLoaders } from '../../../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../../../helpers/FormGeneral';
import CustomInputExt from '../../../../../components/CustomInput/CustomInputExt';
import { ServicesMvneType } from '../../../../../modules/lib/hooks/useOperatorType';

type OplSimCardServiceTypeSmsMsgEditType = {
    open: boolean;
    reload: (data: SimCardServiceTypeConfigurationGet) => void;
    simCardServiceType: SimCardServiceTypeItem;
    close: () => void;
    languageName: string;
    simCardServiceTypeConfiguration: SimCardServiceTypeConfigurationGet | SimCardServiceTypeConfigurationPost | SimCardServiceTypeConfigurationPut;
    isPost: boolean;
    id?: string | null;
    languageId?: number | null;
    platformResellerId: number;
};

export const OplSimCardServiceTypeSmsMsgEdit: FunctionComponent<OplSimCardServiceTypeSmsMsgEditType> = ({
    open,
    reload,
    simCardServiceType,
    close,
    simCardServiceTypeConfiguration,
    languageName,
    isPost,
    id,
    languageId,
    platformResellerId,
}): ReactElement => {
    const dispatch = useDispatch();
    const [configuration, setConfiguration] = useState<SimCardServiceItemConfiguration>(JSON.parse(simCardServiceTypeConfiguration.configuration));
    const save = (): void => {
        simCardServiceTypeConfiguration.configuration = JSON.stringify(configuration);
        const itemLoader = new ItemLoaders(dispatch);
        if (!isPost) {
            itemLoader.putWithCallback(
                FormGeneralHelper.getByUri(id ?? 'empty'),
                'no-dispatch',
                simCardServiceTypeConfiguration,
                (data: SimCardServiceTypeConfigurationGet): void => {
                    reload(data);
                    close();
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        } else {
            const simCardServiceTypeConfigurationPost: SimCardServiceTypeConfigurationPost = {
                configuration: simCardServiceTypeConfiguration.configuration,
                simCardServiceType: '/base-api/api/platform/sim_card_service_types/' + simCardServiceType.id.toString(),
                systemLanguage: '/base-api/api/platform/system_languages/' + (languageId ?? -1).toString(),
                platformReseller: '/base-api/api/platform/platform_resellers/' + platformResellerId.toString(),
            };
            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_configurations')),
                'no-dispatch',
                simCardServiceTypeConfigurationPost,
                (data: SimCardServiceTypeConfigurationGet): void => {
                    reload(data);
                    close();
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = parseInt(event.target.value);
        setConfiguration({ ...configuration, [field]: selected === 1 });
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        setConfiguration({ ...configuration, [field]: value });
    };

    return (
        <Dialog open={open} maxWidth="md" onClose={close} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle>
                <I18n trans="prov:feature.sms_message_configure_with_language" valueObject={{ language: languageName.toUpperCase() }} />
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12} style={{ paddingTop: '20px' }}>
                        <I18n trans="prov:feature.sms_allowed_variables" />
                    </GridItem>
                    <GridItem xs={12} style={{ paddingTop: '20px' }}>
                        <SimpleSelect
                            label="prov:feature.package_activation"
                            fieldName={PACKAGE_ACTIVATE}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PACKAGE_ACTIVATE ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PACKAGE_ACTIVATE_MSG"
                            value={configuration.PACKAGE_ACTIVATE_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    {simCardServiceType.serviceType === ServicesMvneType.BME && (
                        <>
                            <GridItem xs={12} style={{ paddingTop: '20px' }}>
                                <SimpleSelect
                                    label="prov:feature.usage_80_percent"
                                    fieldName={PACKAGE_USAGE_80_PERCENT}
                                    required={true}
                                    elements={getBooleanElementList()}
                                    dispatchName={'SMS_MESSAGE'}
                                    selectedValue={configuration.PACKAGE_USAGE_80_PERCENT ? 1 : 2}
                                    callback={onChangeSelectEvent}
                                />
                            </GridItem>
                            <GridItem xs={12}>
                                <CustomInputExt
                                    formControlProps={{ fullWidth: true }}
                                    required={true}
                                    labelText={'prov:feature.package_sms'}
                                    name="PACKAGE_USAGE_80_PERCENT_MSG"
                                    value={configuration.PACKAGE_USAGE_80_PERCENT_MSG ?? ''}
                                    onChange={onChangeEvent}
                                />
                            </GridItem>
                            <GridItem xs={12} style={{ paddingTop: '20px' }}>
                                <SimpleSelect
                                    label="prov:feature.usage_all_percent"
                                    fieldName={PACKAGE_USAGE_ALL}
                                    required={true}
                                    elements={getBooleanElementList()}
                                    dispatchName={'SMS_MESSAGE'}
                                    selectedValue={configuration.PACKAGE_USAGE_ALL ? 1 : 2}
                                    callback={onChangeSelectEvent}
                                />
                            </GridItem>
                            <GridItem xs={12}>
                                <CustomInputExt
                                    formControlProps={{ fullWidth: true }}
                                    required={true}
                                    labelText={'prov:feature.package_sms'}
                                    name="PACKAGE_USAGE_ALL_MSG"
                                    value={configuration.PACKAGE_USAGE_ALL_MSG ?? ''}
                                    onChange={onChangeEvent}
                                />
                            </GridItem>
                            <GridItem xs={12} style={{ paddingTop: '20px' }}>
                                <SimpleSelect
                                    label="prov:feature.package_near_expire"
                                    fieldName={PACKAGE_NEAR_EXPIRE}
                                    required={true}
                                    elements={getBooleanElementList()}
                                    dispatchName={'SMS_MESSAGE'}
                                    selectedValue={configuration.PACKAGE_NEAR_EXPIRE ? 1 : 2}
                                    callback={onChangeSelectEvent}
                                />
                            </GridItem>
                            <GridItem xs={12}>
                                <CustomInputExt
                                    formControlProps={{ fullWidth: true }}
                                    required={true}
                                    labelText={'prov:feature.package_sms'}
                                    name="PACKAGE_NEAR_EXPIRE_MSG"
                                    value={configuration.PACKAGE_NEAR_EXPIRE_MSG ?? ''}
                                    onChange={onChangeEvent}
                                />
                            </GridItem>
                        </>
                    )}
                    <GridItem xs={12} style={{ paddingTop: '20px' }}>
                        <SimpleSelect
                            label="prov:feature.package_expire"
                            fieldName={PACKAGE_EXPIRE}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PACKAGE_EXPIRE ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PACKAGE_EXPIRE_MSG"
                            value={configuration.PACKAGE_EXPIRE_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom simple callback={close} callbackParam={{}} label="general:common.close" size="sm" />
                <CustomTextButtom callback={save} callbackParam={{}} label="prov:feature.confirm_title" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
